import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import Drawer, { FormField, getSaveLabel } from "@/components/Drawer";
import {
  Plus,
  PencilSimple,
  Trash,
  ArrowClockwise,
  StackSimple,
  CheckCircle,
  XCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const GENERAL_ID = "general";
const EMPTY = { name: "", code: "", description: "", active: true, is_sub_dealer: false };

function TypeFormDrawer({ open, editing, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editing ? {
      name: editing.name || "",
      code: editing.code || "",
      description: editing.description || "",
      active: editing.active !== false,
      is_sub_dealer: !!editing.is_sub_dealer,
    } : EMPTY);
  }, [open, editing]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, code: (form.code || "").toUpperCase() };
      if (editing) {
        await api.put(`/branch-types/${editing.id}`, payload);
        toast.success("Branch type updated");
      } else {
        await api.post("/branch-types", payload);
        toast.success("Branch type added");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow={`${editing ? "Edit" : "New"} Branch Type`}
      title={editing ? editing.name : "Add a branch type"}
      testIdClose="close-branch-type-form"
    >
      <form onSubmit={handleSubmit} data-testid="branch-type-form" className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="bt-name" required />
          <FormField label="Code" value={form.code} onChange={(v) => setForm({ ...form, code: v.toUpperCase() })} testId="bt-code" required mono />
        </div>
        <FormField
          label="Description"
          value={form.description}
          onChange={(v) => setForm({ ...form, description: v })}
          testId="bt-description"
          placeholder="e.g., Urban premium showrooms"
        />
        <label className="inline-flex items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => setForm({ ...form, active: e.target.checked })}
            data-testid="bt-active"
          />
          Active
        </label>
        <label className="inline-flex items-start gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={form.is_sub_dealer}
            onChange={(e) => setForm({ ...form, is_sub_dealer: e.target.checked })}
            data-testid="bt-is-sub-dealer"
            className="mt-0.5"
          />
          <span>
            Sub-Dealer (B2B)
            <div className="text-[11px] text-ink-muted">
              Branches of this type appear under <strong>Sub-Dealers</strong> with their own ledger, invoices, and GST billing.
            </div>
          </span>
        </label>
        <div className="pt-4 flex items-center gap-2 justify-end border-t border-canvas-line -mx-6 px-6">
          <button type="button" onClick={onClose} className="px-4 py-2.5 border border-canvas-line text-sm hover:border-ink">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="save-branch-type"
            className="px-4 py-2.5 bg-ink text-white text-sm font-semibold hover:bg-brand transition-colors disabled:opacity-60"
          >
            {getSaveLabel(saving, editing, "Saving…", "Save changes", "Add type")}
          </button>
        </div>
      </form>
    </Drawer>
  );
}

export default function BranchTypesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/branch-types");
      setRows(data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSaved = () => {
    setShowForm(false);
    load();
  };

  const handleToggle = async (t) => {
    try {
      if (t.active) {
        if (!window.confirm(`Deactivate "${t.name}"? It must have no active branches.`)) return;
        await api.delete(`/branch-types/${t.id}`);
        toast.success("Branch type deactivated");
      } else {
        await api.put(`/branch-types/${t.id}`, { ...t, active: true });
        toast.success("Branch type reactivated");
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="06 / Branch Types"
        title="Branch Types"
        subtitle="Group branches by character (city, highway, rural). Each type owns its own package catalog — same package name can carry different prices across types."
        actions={
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            data-testid="add-branch-type-button"
            className="bg-ink text-white px-4 py-2.5 text-sm font-semibold tracking-tight hover:bg-brand transition-colors duration-150 flex items-center gap-2"
          >
            <Plus size={14} weight="bold" />
            Add Type
          </button>
        }
      />

      <div className="px-8 py-6">
        <div className="bg-canvas-card border border-canvas-line overflow-hidden">
          <table className="w-full" data-testid="branch-types-table">
            <thead>
              <tr className="border-b border-canvas-line bg-canvas-panel">
                {["Name", "Code", "Description", "Status", "Created", ""].map((h) => (
                  <th key={h} className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center font-mono text-xs text-ink-subtle">Loading…</td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <StackSimple size={24} weight="bold" className="text-ink-subtle mx-auto mb-2" />
                    <div className="font-mono text-xs text-ink-subtle">No branch types yet.</div>
                  </td>
                </tr>
              )}
              {rows.map((t) => {
                const isDefault = t.id === GENERAL_ID;
                return (
                  <tr key={t.id} data-testid={`branch-type-row-${t.id}`} className="border-b border-canvas-line hover:bg-canvas-panel transition-colors">
                    <td className="px-4 py-3 text-sm font-semibold text-ink">
                      {t.name}
                      {isDefault && (
                        <div className="text-[10px] font-mono uppercase tracking-wider text-brand mt-1">Default</div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-ink">{t.code}</td>
                    <td className="px-4 py-3 text-sm text-ink-muted">{t.description || "—"}</td>
                    <td className="px-4 py-3">
                      {t.active ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase text-[#00B341]">
                          <CheckCircle size={12} weight="fill" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase text-ink-subtle">
                          <XCircle size={12} weight="fill" /> Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-ink-muted">{fmtDate(t.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => { setEditing(t); setShowForm(true); }}
                          data-testid={`edit-branch-type-${t.id}`}
                          className="p-1.5 border border-canvas-line hover:border-ink"
                          aria-label="Edit"
                        >
                          <PencilSimple size={13} weight="bold" />
                        </button>
                        {!isDefault && (
                          <button
                            onClick={() => handleToggle(t)}
                            data-testid={`toggle-branch-type-${t.id}`}
                            className="p-1.5 border border-canvas-line hover:border-destructive hover:text-destructive"
                            aria-label={t.active ? "Deactivate" : "Reactivate"}
                            title={t.active ? "Deactivate" : "Reactivate"}
                          >
                            {t.active ? <Trash size={13} weight="bold" /> : <ArrowClockwise size={13} weight="bold" />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <TypeFormDrawer
        open={showForm}
        editing={editing}
        onClose={() => setShowForm(false)}
        onSaved={handleSaved}
      />
    </div>
  );
}
